import { Grid } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import AdminEntityManager from '../../components/admin-entities/admin-entity-manager/admin-entity-manager.component';
import SelectPartnerFilter from '../../components/filters/select-partner-filter.component';
import SelectResellerFilter from '../../components/filters/select-reseller-filter.component';
import { Entity } from '../../enums/entity.enums';
import { Roles } from '../../enums/roles-enum';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { IPaginationMeta } from '../../type/admin.types';
import {
  BasicMerchant,
  RetrieveAllEntityRequest,
  RetrieveAllMerchantsResponse,
} from '../../type/entity.types';
import { ErrorResponse } from '../../type/error-response.types';
import { EntityFilterData, EntityIdFilterData } from '../../type/filter.types';

function AdminMerchantsPage() {
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const [merchantsData, setMerchantsData] = useState<BasicMerchant[]>();
  const [paginationData, setPaginationData] = useState<IPaginationMeta>();
  const filterSelectData = useForm<EntityFilterData>({
    initialValues: {
      resellersData: [],
    },
  });
  const filterValues = useForm<EntityIdFilterData>({
    initialValues: {
      reseller: '',
    },
  });
  const [loadMerchantsParams, setLoadMerchantsParams] =
    useState<RetrieveAllEntityRequest>({
      page: 1,
      limit: 20,
    });
  const [refreshTable, setRefreshTable] = useState(false);
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadMerchants = async () => {
    let params: LoadMerchantsParams = {
      ...loadMerchantsParams,
    };

    if (!loadMerchantsParams?.resellerId) {
      delete params.resellerId;
    }
    if (!loadMerchantsParams?.partnerId) {
      delete params.partnerId;
    }

    delete params.searchEntity;
    if (loadMerchantsParams.searchEntity?.trim()) {
      params.merchantName = loadMerchantsParams.searchEntity;
    } else {
      delete params.merchantName;
    }

    try {
      setIsLoading(true);
      const response = await axiosPrivate.get('web/merchants', {
        params,
      });

      const data: RetrieveAllMerchantsResponse = response.data;
      const merchants: BasicMerchant[] = data?.items;

      setPaginationData(data?.metadata);
      return merchants;
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-load-merchants-data',
        message:
          error.error || err?.response?.data || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadAndSetMerchants = async () => {
    const merchants = await loadMerchants();
    setMerchantsData(merchants);
  };

  const paginationRequest = (page: number, limit: number) => {
    setLoadMerchantsParams((prev) => {
      return {
        ...prev,
        page,
        limit,
      };
    });
  };

  useDidMountEffect(() => {
    let isInvalidFilters = false;

    if (
      auth?.role === Roles.ADMINISTRATOR ||
      auth?.role === Roles.SETTLEMENT ||
      auth?.role === Roles.CHECKER
    ) {
      isInvalidFilters =
        filterValues.values.reseller === loadMerchantsParams.resellerId &&
        filterValues.values.partner === loadMerchantsParams.partnerId;
    } else if (auth?.role === Roles.PARTNER) {
      isInvalidFilters =
        filterValues.values.reseller === loadMerchantsParams.resellerId;
    }

    if (isInvalidFilters) {
      return;
    }

    setLoadMerchantsParams((prev) => {
      return {
        ...prev,
        partnerId: filterValues.values.partner,
        resellerId: filterValues.values.reseller,
        page: 1,
      };
    });

    return () => {};
  }, [filterValues.values]);

  useDidMountEffect(() => {
    loadAndSetMerchants();
    setCheckedAll(false);
    setIsChecked([]);
  }, [loadMerchantsParams]);

  useDidMountEffect(() => {
    loadAndSetMerchants();
  }, [refreshTable]);

  useEffect(() => {
    loadAndSetMerchants();
  }, []);

  return (
    <>
      <AdminEntityManager
        entityType={Entity.MERCHANT}
        merchantsData={merchantsData}
        paginationRequest={paginationRequest}
        paginationData={paginationData}
        entityParams={loadMerchantsParams}
        setEntityParams={setLoadMerchantsParams}
        setRefreshTable={setRefreshTable}
        checkedAll={checkedAll}
        setCheckedAll={setCheckedAll}
        isChecked={isChecked}
        setIsChecked={setIsChecked}
        filter={
          <>
            <Grid>
              {(auth?.role === Roles.ADMINISTRATOR ||
                auth?.role === Roles.SETTLEMENT ||
                auth?.role === Roles.CHECKER) && (
                <Grid.Col xs={12} sm={6}>
                  <SelectPartnerFilter
                    filterSelectData={filterSelectData}
                    filterValues={filterValues}
                  />
                </Grid.Col>
              )}
              {(auth?.role === Roles.PARTNER ||
                auth?.role === Roles.ADMINISTRATOR ||
                auth?.role === Roles.SETTLEMENT ||
                auth?.role === Roles.CHECKER) && (
                <Grid.Col xs={12} sm={6}>
                  <SelectResellerFilter
                    filterSelectData={filterSelectData}
                    filterValues={filterValues}
                    isPartnerDependent={
                      auth?.role === Roles.ADMINISTRATOR ||
                      auth?.role === Roles.SETTLEMENT ||
                      auth?.role === Roles.CHECKER
                    }
                  />
                </Grid.Col>
              )}
            </Grid>
          </>
        }
        isLoading={isLoading}
      />
    </>
  );
}

export default AdminMerchantsPage;

interface LoadMerchantsParams extends RetrieveAllEntityRequest {
  merchantName?: string;
}
